// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect, useRef, useCallback, useState } from 'react';
import { api } from './api';
import { logger } from './logger';
import {
  ConnectionStatus,
  type ConnectionStatusType,
  useWebSocketRefs,
  useHeartbeat,
  canConnect,
  buildWebSocketUrl,
  getReconnectDelay,
  clearReconnectTimeout,
} from './useWebSocketBase';
import { wsRegistry } from './wsRegistry';
import {
  WS_MAX_RECONNECT_ATTEMPTS,
  WS_MAX_AUTH_RETRIES,
  WS_MAX_SESSION_RETRIES,
} from '../constants';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { ClientActionEnum, ServerActionEnum, type WebSocketMessage } from '../types/game';
import { validateWebSocketMessage } from './validation/websocketMessage';

// Re-export for backward compatibility
export { ConnectionStatus };
export type { ConnectionStatusType };

interface UseWebSocketOptions {
  sessionId: string;
  playerId: string;
  playerToken: string;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onOpen?: () => void;
  onFatalError?: (code: number, reason: string) => void;
}

export function useWebSocket({
  sessionId,
  playerId,
  playerToken,
  onMessage,
  onError,
  onClose,
  onOpen,
  onFatalError,
}: UseWebSocketOptions) {
  // Use shared refs from base hook
  const {
    wsRef,
    reconnectTimeout,
    reconnectAttempts,
    heartbeatIntervalRef,
    missedPongsRef,
    connectRef,
    pendingPingsRef,
  } = useWebSocketRefs();
  const [status, setStatus] = useState<ConnectionStatusType>(ConnectionStatus.DISCONNECTED);
  const maxReconnectAttempts = WS_MAX_RECONNECT_ATTEMPTS;
  const authRetryAttempts = useRef(0);
  const maxAuthRetries = WS_MAX_AUTH_RETRIES;
  const sessionRetryAttempts = useRef(0);
  const maxSessionRetries = WS_MAX_SESSION_RETRIES;

  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);
  const onFatalErrorRef = useRef(onFatalError);

  // Keep refs updated with latest callbacks to avoid stale closure issues
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useEffect(() => {
    onFatalErrorRef.current = onFatalError;
  }, [onFatalError]);

  const sendMessage = useCallback(
    (action: ClientActionEnum | string, payload: Record<string, unknown> = {}) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const message = { action, payload };
        if (action !== ClientActionEnum.PING) {
          logger.debug('websocket_send_message', { action });
        }
        wsRef.current.send(JSON.stringify(message));
      } else {
        logger.warn('websocket_send_failed_not_connected', { action });
      }
    },
    [wsRef]
  );

  const requestResync = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      logger.warn('websocket_resync_skipped_not_connected');
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        action: ClientActionEnum.CLIENT_READY,
        payload: { last_seen_sequence_id: 0 },
      })
    );
  }, [wsRef]);

  // Callback to update latency in gameStore
  const handleRttMeasured = useCallback((rtt: number) => {
    useGameStore.getState().updateLatency(rtt);
  }, []);

  // Use shared heartbeat management
  const { startHeartbeat, stopHeartbeat, handlePong } = useHeartbeat(
    wsRef,
    heartbeatIntervalRef,
    missedPongsRef,
    pendingPingsRef,
    sendMessage,
    handleRttMeasured
  );

  const connect = useCallback(() => {
    if (!sessionId || !playerId || !playerToken || typeof window === 'undefined') {
      return;
    }
    if (!canConnect(wsRef)) {
      return;
    }

    try {
      setStatus(ConnectionStatus.CONNECTING);

      const url = buildWebSocketUrl(`/ws/${sessionId}/${playerId}`);
      logger.debug('websocket_connecting_via_proxy', { url });

      wsRef.current = new WebSocket(url, [playerToken]);

      wsRef.current.onopen = () => {
        logger.info('websocket_connected', { sessionId, playerId });
        setStatus(ConnectionStatus.CONNECTED);
        useGameStore.getState().setConnected(true);
        reconnectAttempts.current = 0;
        authRetryAttempts.current = 0; // Reset auth retries on successful connection
        sessionRetryAttempts.current = 0; // Reset session retries on successful connection
        startHeartbeat();
        const lastSeenSequence = useGameStore.getState().lastSeenSequence;
        const readyMessage = {
          action: ClientActionEnum.CLIENT_READY,
          payload: { last_seen_sequence_id: lastSeenSequence },
        };
        wsRef.current?.send(JSON.stringify(readyMessage));
        onOpenRef.current?.();
      };

      wsRef.current.onmessage = event => {
        try {
          const parsed = JSON.parse(event.data);
          const message = validateWebSocketMessage(parsed);
          if (!message) {
            logger.warn('websocket_invalid_message', { data: event.data.slice(0, 200) });
            return;
          }
          if (message.action === ServerActionEnum.PONG) {
            handlePong();
            return;
          }
          onMessageRef.current?.(message);
        } catch (error) {
          logger.error('websocket_parse_error', { error: String(error) });
        }
      };

      wsRef.current.onerror = error => {
        logger.error('websocket_error', {
          url,
          readyState: wsRef.current?.readyState,
        });
        setStatus(ConnectionStatus.ERROR);
        onErrorRef.current?.(error);
      };

      wsRef.current.onclose = async event => {
        if (!wsRef.current) {
          // This means disconnect() was called, so it's an intentional closure.
          logger.debug('websocket_closed_intentional');
          return;
        }

        logger.debug('websocket_closed_unexpected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        setStatus(ConnectionStatus.DISCONNECTED);
        useGameStore.getState().setConnected(false);
        stopHeartbeat();
        onCloseRef.current?.();

        // Handle 4003 (auth required) with retry - try to get fresh token
        if (event.code === 4003 && authRetryAttempts.current < maxAuthRetries) {
          authRetryAttempts.current++;
          logger.info('websocket_auth_retry', {
            attempt: authRetryAttempts.current,
            maxAttempts: maxAuthRetries,
          });

          try {
            // Try to rejoin the room to get a fresh token
            const accessToken = useAuthStore.getState().accessToken;
            const displayName = useGameStore.getState().displayName || 'Player';

            if (accessToken && sessionId) {
              const response = await api.joinRoom({
                session_id: sessionId,
                display_name: displayName,
              });
              if (response.player_token) {
                // Update token and reconnect
                useGameStore.getState().setPlayerToken(response.player_token);
                logger.info('websocket_auth_token_refreshed', { sessionId });
                // Reconnect with fresh token after short delay
                reconnectTimeout.current = setTimeout(() => connectRef.current(), 500);
                return;
              }
            }
          } catch (error) {
            logger.warn('websocket_auth_retry_failed', {
              error: String(error),
            });
          }
        }

        // Handle 4004 (session not found) with retry - re-join to refresh session state
        // This handles race conditions where session isn't fully ready when WebSocket connects
        if (event.code === 4004 && sessionRetryAttempts.current < maxSessionRetries) {
          sessionRetryAttempts.current++;
          logger.info('websocket_session_retry', {
            attempt: sessionRetryAttempts.current,
            maxAttempts: maxSessionRetries,
          });

          try {
            // Try to rejoin the room to ensure session exists
            const accessToken = useAuthStore.getState().accessToken;
            const displayName = useGameStore.getState().displayName || 'Player';

            if (accessToken && sessionId) {
              const response = await api.joinRoom({
                session_id: sessionId,
                display_name: displayName,
              });
              if (response.player_token) {
                // Update token and reconnect with exponential backoff
                useGameStore.getState().setPlayerToken(response.player_token);
                logger.info('websocket_session_rejoin_success', { sessionId });
                const delay = 500 * sessionRetryAttempts.current;
                reconnectTimeout.current = setTimeout(() => connectRef.current(), delay);
                return;
              }
            }
          } catch (error) {
            logger.warn('websocket_session_retry_failed', {
              error: String(error),
              attempt: sessionRetryAttempts.current,
            });
          }
        }

        // Fatal errors that should not retry (removed 4003 and 4004 - handled above)
        const fatalErrorCodes = [1008];
        if (fatalErrorCodes.includes(event.code)) {
          logger.error('websocket_fatal_error', {
            code: event.code,
            reason: event.reason,
          });
          onFatalErrorRef.current?.(event.code, event.reason);
          return; // Do not reconnect on fatal errors
        }

        // If 4003 and exhausted retries, treat as fatal
        if (event.code === 4003 && authRetryAttempts.current >= maxAuthRetries) {
          logger.error('websocket_auth_retries_exhausted', {
            maxAttempts: maxAuthRetries,
          });
          onFatalErrorRef.current?.(event.code, event.reason);
          return;
        }

        // If 4004 and exhausted retries, treat as fatal
        if (event.code === 4004 && sessionRetryAttempts.current >= maxSessionRetries) {
          logger.error('websocket_session_retries_exhausted', {
            maxAttempts: maxSessionRetries,
          });
          onFatalErrorRef.current?.(event.code, event.reason);
          return;
        }

        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = getReconnectDelay(reconnectAttempts.current, 10000);
          logger.info('websocket_reconnecting', {
            delay,
            attempt: reconnectAttempts.current,
            maxAttempts: maxReconnectAttempts,
          });
          reconnectTimeout.current = setTimeout(() => connectRef.current(), delay);
        } else {
          logger.error('websocket_max_reconnects_reached', {
            maxAttempts: maxReconnectAttempts,
          });
          onFatalErrorRef.current?.(0, 'Max reconnection attempts reached');
        }
      };
    } catch (error) {
      logger.error('websocket_create_failed', { error: String(error) });
      setStatus(ConnectionStatus.ERROR);
    }
  }, [
    sessionId,
    playerId,
    playerToken,
    startHeartbeat,
    stopHeartbeat,
    wsRef,
    reconnectAttempts,
    handlePong,
    connectRef,
    reconnectTimeout,
    maxReconnectAttempts,
    maxAuthRetries,
    maxSessionRetries,
  ]);

  useEffect(() => {
    connectRef.current = connect;
  });

  const disconnect = useCallback(() => {
    clearReconnectTimeout(reconnectTimeout);
    stopHeartbeat();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus(ConnectionStatus.DISCONNECTED);
    useGameStore.getState().setConnected(false);
  }, [reconnectTimeout, stopHeartbeat, wsRef]);

  useEffect(() => {
    if (sessionId && playerId && playerToken) {
      // Intentional connection setup - not a cascading render issue

      // eslint-disable-next-line react-hooks/set-state-in-effect
      connect();
    }
    return () => {
      disconnect();
    };
  }, [sessionId, playerId, playerToken, connect, disconnect]);

  useEffect(() => {
    let didRegister = false;
    if (status === ConnectionStatus.CONNECTED && sessionId && playerId && sendMessage) {
      wsRegistry.register(sessionId, playerId, sendMessage);
      didRegister = true;
    }
    return () => {
      if (didRegister && sessionId) {
        wsRegistry.unregister(sessionId);
      }
    };
  }, [status, sessionId, playerId, sendMessage]);

  return {
    status,
    sendMessage,
    connect,
    disconnect,
    requestResync,
  };
}
